import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import TopHeader from "../components/layout/TopHeader";
import ButtonEnhanced from "../components/common/ButtonEnhanced";
import ClearFiltersButton from "../components/common/ClearFiltersButton";
import BusFiltersCard from "../components/reports/BusFiltersCard";
import Modal from "../components/layout/Modal";
import { getAllBusesConnectionMetricsList } from "../services/statisticsService";
import { getBusSchedulesByBusIds, bulkUpsertBusSchedules } from "../services/cmsService";
import { getBusActivityByBusIds, getRealtimeArrivalsByBusIds, subscribeBusActivity } from "../services/busActivityService";
import { exportBusScheduleTemplate, parseBusScheduleExcel } from "../utils/busScheduleExcel";

// Display order matches Mon..Sun. Each day is paired with the BusSchedule boolean field
// it maps to, so we can look the schedule up directly by day index.
const DAY_COLUMNS = [
  { label: "L", full: "Lunes", scheduleKey: "bshMonday" },
  { label: "M", full: "Martes", scheduleKey: "bshTuesday" },
  { label: "M", full: "Miércoles", scheduleKey: "bshWednesday" },
  { label: "J", full: "Jueves", scheduleKey: "bshThursday" },
  { label: "V", full: "Viernes", scheduleKey: "bshFriday" },
  { label: "S", full: "Sábado", scheduleKey: "bshSaturday" },
  { label: "D", full: "Domingo", scheduleKey: "bshSunday" },
];

// Returns Monday 00:00 local time for the current week (Mon-Sun layout).
function getWeekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const shift = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + shift);
  return d;
}

function formatIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatPhone(phone) {
  if (!phone) return "—";
  const cleaned = phone.replace(/\s+/g, "");
  const match = cleaned.match(/^(\+\d{2})(\d{3})(\d{7})$/);
  return match ? `${match[1]} ${match[2]}-${match[3]}` : phone;
}

function mapBus(item) {
  const bus = item.bus || {};
  const route = bus.route || {};
  const superRoute = bus.superRoute || {};
  const cityInfo = superRoute.city || route.city || route.startStop?.city || {};
  const stateInfo = superRoute.state || cityInfo.state || route.state || {};

  const driversList = item.users || [];
  const owner = bus.busOwner?.user;

  let driverDisplay = "Sin conductor";
  let driverPhone = "—";
  if (driversList.length === 1) {
    const u = driversList[0];
    driverDisplay = `${u.userName || ""} ${u.userLastName || ""}`.trim() || "Sin nombre";
    driverPhone = formatPhone(u.userPhone);
  } else if (driversList.length > 1) {
    driverDisplay = `${driversList.length} conductores`;
  }

  let ownerDisplay = "—";
  let ownerPhone = "—";
  if (owner) {
    ownerDisplay = `${owner.userName || ""} ${owner.userLastName || ""}`.trim() || "Sin nombre";
    ownerPhone = formatPhone(owner.userPhone);
  }

  return {
    id: bus.busId,
    name: bus.busName || `Unidad ${bus.busRegistrationId || bus.busId}`,
    plate: bus.busRegistrationId || "Sin Placa",
    brand: superRoute.surName || "Sin Línea",
    route: route.rouName || "Sin Ruta",
    city: cityInfo.citName || "Sin Ciudad",
    surId: superRoute.surId || null,
    rouId: route.rouId || null,
    citId: cityInfo.citId || null,
    staId: stateInfo.staId || null,
    staName: stateInfo.staName || null,
    driverDisplay,
    driverPhone,
    ownerDisplay,
    ownerPhone,
  };
}

// Color resolution for one (bus, dayIndex) cell.
// `worked` = there is a DailyActivity row for that day (or a realtime arrival flag).
// `scheduled` = the BusSchedule says this bus should run that day.
// Future days: paint scheduled cells in a soft amber so the user sees what's planned but
// nothing is judged as "missed" yet; non-scheduled future cells stay neutral.
function resolveCell(scheduled, worked, isFuture) {
  if (isFuture) {
    return scheduled
      ? { bg: "#FEF3C7", color: "#92400E", label: "Programado (pendiente)" }
      : { bg: "#F9FAFB", color: "#9CA3AF", label: "No programado" };
  }
  if (scheduled && worked) return { bg: "#10B981", color: "white", label: "Trabajó (programado)" };
  if (scheduled && !worked) return { bg: "#EF4444", color: "white", label: "No trabajó (estaba programado)" };
  if (!scheduled && worked) return { bg: "#F59E0B", color: "white", label: "Trabajó (no programado)" };
  return { bg: "#F3F4F6", color: "#9CA3AF", label: "No programado" };
}

export default function PlanningPage() {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [universe, setUniverse] = useState([]);
  const [isLoadingUniverse, setIsLoadingUniverse] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Schedule lookup keyed by busId; populated only when a route is selected.
  const [schedules, setSchedules] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);

  // Two persisted activity sources merged for cell coloring:
  // - activities: DailyActivity (rebuilt nightly from BusDriverData logs) — past days.
  // - persistedArrivals: RealtimeBusArrival (recorded the moment a bus hits a terminal) — today.
  // - realtimeKeys: live arrivals received via WS while the page is open.
  // All three are reduced into the activityKeys Set ("busId:YYYY-MM-DD") used by ScheduleTable.
  const [activities, setActivities] = useState([]);
  const [persistedArrivals, setPersistedArrivals] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [realtimeKeys, setRealtimeKeys] = useState(new Set());

  const [modal, setModal] = useState({ isOpen: false, title: "", message: "", type: "info" });
  const showModal = (title, message, type = "info") =>
    setModal({ isOpen: true, title, message, type });
  const closeModal = () => setModal((m) => ({ ...m, isOpen: false }));

  const handleStateChange = (v) => {
    setSelectedState(v);
    setSelectedCity(null);
    setSelectedLine(null);
    setSelectedRoute(null);
  };
  const handleCityChange = (v) => {
    setSelectedCity(v);
    setSelectedLine(null);
    setSelectedRoute(null);
  };
  const handleLineChange = (v) => {
    setSelectedLine(v);
    setSelectedRoute(null);
  };

  const handleClearFilters = () => {
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedLine(null);
    setSelectedRoute(null);
  };

  const hasActiveFilters =
    selectedState != null || selectedCity != null || selectedLine != null || selectedRoute != null;

  // Universe load — single unpaged call. The previous bulk-pagination pattern issued
  // 100+ requests per visit and brought the backend down because every page loads a
  // huge UserSuperRoute join graph; the *-list endpoint returns the same data in one shot.
  useEffect(() => {
    const fetchUniverse = async () => {
      if (!token) return;
      setIsLoadingUniverse(true);
      try {
        const list = await getAllBusesConnectionMetricsList(token);
        if (!Array.isArray(list)) {
          setUniverse([]);
          return;
        }
        const seen = new Set();
        const deduped = list.filter((it) => {
          const id = it?.bus?.busId;
          if (id == null) return true;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        setUniverse(deduped.map(mapBus));
      } finally {
        setIsLoadingUniverse(false);
      }
    };
    fetchUniverse();
  }, [token]);

  const filteredBuses = useMemo(() => {
    return universe.filter((bus) => {
      if (bus.surId == null || bus.rouId == null) return false;
      if (selectedState != null && bus.staId !== selectedState) return false;
      if (selectedCity != null && bus.citId !== selectedCity) return false;
      if (selectedLine != null && bus.surId !== selectedLine) return false;
      if (selectedRoute != null && bus.rouId !== selectedRoute) return false;
      return true;
    });
  }, [universe, selectedState, selectedCity, selectedLine, selectedRoute]);

  // Current week (Mon..Sun). Computed once per render — cheap enough.
  const weekDates = useMemo(() => {
    const start = getWeekStart(new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, []);

  const todayIso = formatIsoDate(new Date());

  // Fetch schedules + activities + realtime arrivals whenever the route changes.
  useEffect(() => {
    let cancelled = false;
    if (selectedRoute == null || filteredBuses.length === 0) {
      setSchedules([]);
      setActivities([]);
      setPersistedArrivals([]);
      setRealtimeKeys(new Set());
      return () => {};
    }

    const busIds = filteredBuses.map((b) => b.id).filter((id) => id != null);
    if (busIds.length === 0) return () => {};

    const fromIso = formatIsoDate(weekDates[0]);
    const toIso = formatIsoDate(weekDates[6]);

    (async () => {
      setIsLoadingSchedules(true);
      setIsLoadingActivities(true);
      try {
        const [scheds, acts, arrivals] = await Promise.all([
          getBusSchedulesByBusIds(token, busIds),
          getBusActivityByBusIds(token, busIds, fromIso, toIso),
          getRealtimeArrivalsByBusIds(token, busIds, fromIso, toIso),
        ]);
        if (cancelled) return;
        setSchedules(Array.isArray(scheds) ? scheds : []);
        setActivities(Array.isArray(acts) ? acts : []);
        setPersistedArrivals(Array.isArray(arrivals) ? arrivals : []);
        setRealtimeKeys(new Set());
      } catch (error) {
        if (!cancelled) {
          console.error("Error cargando schedules/actividades:", error);
          setSchedules([]);
          setActivities([]);
          setPersistedArrivals([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSchedules(false);
          setIsLoadingActivities(false);
        }
      }
    })();

    return () => { cancelled = true; };
    // weekDates is derived once per mount so its identity is stable; busIds is derived from filteredBuses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute, token, filteredBuses.length]);

  // Realtime subscription: only active while a route is selected, so we don't waste
  // a websocket on an idle filter screen. The subscriber tags arrivals into a Set
  // keyed by "busId:YYYY-MM-DD" — the table reads from that to flip cells live.
  useEffect(() => {
    if (selectedRoute == null) return undefined;
    const visibleBusIds = new Set(filteredBuses.map((b) => b.id));
    const dispose = subscribeBusActivity(token, {
      onArrival: (msg) => {
        if (!msg || msg.busId == null || !msg.activityDate) return;
        if (!visibleBusIds.has(msg.busId)) return;
        const key = `${msg.busId}:${msg.activityDate}`;
        setRealtimeKeys((prev) => {
          if (prev.has(key)) return prev;
          const next = new Set(prev);
          next.add(key);
          return next;
        });
      },
    });
    return dispose;
  }, [selectedRoute, token, filteredBuses]);

  const scheduleByBusId = useMemo(() => {
    const map = new Map();
    schedules.forEach((s) => {
      const id = s?.bus?.busId;
      if (id != null) map.set(id, s);
    });
    return map;
  }, [schedules]);

  const activityKeys = useMemo(() => {
    const set = new Set();
    activities.forEach((a) => {
      if (a?.busId != null && a?.activityDate) {
        set.add(`${a.busId}:${a.activityDate}`);
      }
    });
    persistedArrivals.forEach((a) => {
      if (a?.busId != null && a?.arrivalDate) {
        set.add(`${a.busId}:${a.arrivalDate}`);
      }
    });
    return set;
  }, [activities, persistedArrivals]);

  const handleDownloadTemplate = async () => {
    if (selectedLine == null || filteredBuses.length === 0 || isDownloading) return;
    setIsDownloading(true);
    try {
      const busIds = filteredBuses.map((b) => b.id);
      const sched = await getBusSchedulesByBusIds(token, busIds);
      const map = new Map();
      (sched || []).forEach((s) => {
        if (s?.bus?.busId != null) map.set(s.bus.busId, s);
      });
      exportBusScheduleTemplate(filteredBuses, map);
    } catch (error) {
      console.error("Error descargando plantilla:", error);
      showModal("Error", "No se pudo descargar la plantilla. Intenta de nuevo.", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUploadClick = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIsUploading(true);
    try {
      const { dtos, errors } = await parseBusScheduleExcel(file);
      if (errors.length > 0) {
        showModal(
          "Excel inválido",
          `Se encontraron problemas:\n\n${errors.slice(0, 5).join("\n")}${errors.length > 5 ? "\n..." : ""}`,
          "error"
        );
        return;
      }
      if (dtos.length === 0) {
        showModal("Excel vacío", "No se encontraron filas con horarios para procesar.", "warning");
        return;
      }
      const saved = await bulkUpsertBusSchedules(token, dtos);
      showModal("Horarios actualizados", `Se guardaron ${saved.length} horarios correctamente.`, "success");
    } catch (error) {
      console.error("Error subiendo plantilla:", error);
      showModal("Error", "No se pudo procesar el archivo. Verifica el formato.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const isLoadingTable = isLoadingSchedules || isLoadingActivities;

  return (
    <>
      <TopHeader title="Planificación de Horarios" />
      <div style={{ flex: 1, backgroundColor: "#FFFFFF", overflowY: "auto" }}>
        <div style={{ padding: "12px 24px 32px", maxWidth: 1600, margin: "0 auto", fontFamily: "system-ui, -apple-system, sans-serif" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 24,
              padding: "16px 18px",
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderRadius: 14,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "#FEF3C7",
                color: "#B45309",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: "#92400E",
                lineHeight: 1.5,
              }}
            >
              Selecciona una ruta para ver la cuadrícula semanal de unidades. Cada celda compara el horario programado con la actividad real en tiempo real.
            </p>
          </div>

          <BusFiltersCard
            selectedState={selectedState}
            selectedCity={selectedCity}
            selectedLine={selectedLine}
            selectedRoute={selectedRoute}
            onStateChange={handleStateChange}
            onCityChange={handleCityChange}
            onLineChange={handleLineChange}
            onRouteChange={setSelectedRoute}
            buses={universe}
            isLoadingUniverse={isLoadingUniverse}
            showDateFilter={false}
            actionsSlot={
              <>
                <ClearFiltersButton
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters || isDownloading || isUploading}
                  style={{ minWidth: 200 }}
                />

                <ButtonEnhanced
                  variant="green"
                  onClick={handleDownloadTemplate}
                  disabled={selectedLine == null || filteredBuses.length === 0 || isDownloading || isUploading}
                  style={{ padding: "10px 20px", fontWeight: 600, minWidth: 200 }}
                  icon={
                    isDownloading ? (
                      <div style={{ width: 16, height: 16, border: "2px solid white", borderTop: "2px solid transparent", borderRadius: "50%", animation: "planSpin 1s linear infinite" }} />
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                      </svg>
                    )
                  }
                >
                  {isDownloading ? "Generando..." : "Descargar plantilla"}
                </ButtonEnhanced>

                <ButtonEnhanced
                  variant="blue"
                  onClick={handleUploadClick}
                  disabled={isUploading || isDownloading || isLoadingUniverse}
                  style={{ padding: "10px 20px", fontWeight: 600, minWidth: 200 }}
                  icon={
                    isUploading ? (
                      <div style={{ width: 16, height: 16, border: "2px solid white", borderTop: "2px solid transparent", borderRadius: "50%", animation: "planSpin 1s linear infinite" }} />
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,13V17H10V13H7L12,8L17,13M19.35,10.03C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.03C2.34,8.36 0,10.9 0,14A6,6 0 0,0 6,20H19A5,5 0 0,0 24,15C24,12.36 21.95,10.22 19.35,10.03Z" />
                      </svg>
                    )
                  }
                >
                  {isUploading ? "Procesando..." : "Subir plantilla"}
                </ButtonEnhanced>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: "none" }}
                  onChange={handleFileSelected}
                />
                <style>{`@keyframes planSpin { 100% { transform: rotate(360deg); } }`}</style>
              </>
            }
          />

          {selectedRoute == null && !isLoadingUniverse && (
            <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", color: "#92400E", padding: "12px 16px", borderRadius: 8, marginBottom: 24, fontSize: 14 }}>
              Selecciona una <strong>Ruta</strong> para ver la cuadrícula semanal de unidades.
            </div>
          )}

          {selectedRoute != null && (
            <Legend />
          )}

          {isLoadingUniverse ? (
            <ScheduleTableSkeleton rows={6} />
          ) : selectedRoute == null ? (
            <div style={{ textAlign: "center", padding: 60, color: "#9CA3AF", fontSize: 14 }}>
              La cuadrícula aparecerá cuando selecciones una ruta.
            </div>
          ) : filteredBuses.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#6B7280" }}>
              No se encontraron buses en la ruta seleccionada.
            </div>
          ) : (
            <ScheduleTable
              buses={filteredBuses}
              weekDates={weekDates}
              todayIso={todayIso}
              scheduleByBusId={scheduleByBusId}
              activityKeys={activityKeys}
              realtimeKeys={realtimeKeys}
              isLoadingTable={isLoadingTable}
              stickyTop={0}
            />
          )}
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={closeModal}
      />
    </>
  );
}

// Loading state for the planning grid: mimics the actual table layout (one wide
// table, not the card cluster used elsewhere) so the page doesn't visually morph
// from cards into a table once data lands.
function ScheduleTableSkeleton({ rows = 6 }) {
  const cellBase = {
    height: 14,
    borderRadius: 4,
    background: "#E5E7EB",
    animation: "planSkeletonPulse 1.4s ease-in-out infinite",
  };
  return (
    <div style={{ borderRadius: 8, background: "white", border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden" }}>
      <style>{`@keyframes planSkeletonPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      <div style={{ display: "grid", gridTemplateColumns: "90px 150px 150px repeat(7, 1fr)", background: "#F9FAFB", padding: "14px 12px", gap: 12, borderBottom: "2px solid #9CA3AF" }}>
        <div style={{ ...cellBase, width: "60%" }} />
        <div style={{ ...cellBase, width: "70%" }} />
        <div style={{ ...cellBase, width: "70%" }} />
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={`h-${i}`} style={{ ...cellBase, width: "50%", justifySelf: "center" }} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={`r-${r}`}
          style={{
            display: "grid",
            gridTemplateColumns: "90px 150px 150px repeat(7, 1fr)",
            padding: "16px 12px",
            gap: 12,
            background: r % 2 === 0 ? "white" : "#FAFAFA",
            borderBottom: "1px solid #D1D5DB",
            alignItems: "center",
          }}
        >
          <div style={{ ...cellBase, width: "70%" }} />
          <div>
            <div style={{ ...cellBase, width: "85%", marginBottom: 6 }} />
            <div style={{ ...cellBase, height: 10, width: "55%" }} />
          </div>
          <div>
            <div style={{ ...cellBase, width: "85%", marginBottom: 6 }} />
            <div style={{ ...cellBase, height: 10, width: "55%" }} />
          </div>
          {Array.from({ length: 7 }).map((_, c) => (
            <div key={`c-${r}-${c}`} style={{ ...cellBase, height: 22, width: "60%", justifySelf: "center" }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function Legend() {
  const items = [
    { color: "#10B981", label: "Trabajó y estaba programado" },
    { color: "#EF4444", label: "No trabajó y estaba programado" },
    { color: "#F59E0B", label: "Trabajó pero no estaba programado" },
    { color: "#FEF3C7", border: "1px solid #FDE68A", label: "Día programado (aún no ocurre)" },
    { color: "#F3F4F6", border: "1px solid #E5E7EB", label: "Sin programación" },
  ];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, padding: "12px 16px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, marginBottom: 16 }}>
      {items.map((it) => (
        <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#374151" }}>
          <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 4, background: it.color, border: it.border || "none" }} />
          {it.label}
        </div>
      ))}
    </div>
  );
}

function ScheduleTable({ buses, weekDates, todayIso, scheduleByBusId, activityKeys, realtimeKeys, isLoadingTable, stickyTop = 0 }) {
  // Single grid line color used on every interior edge so the table reads like an Excel sheet.
  const GRID = "1px solid #D1D5DB";

  // border-collapse: collapse drops borders on sticky <th> cells in most browsers,
  // so we fake them with inset box-shadows that survive sticky positioning. The top
  // shadow matters once the wrapper's top border has scrolled off-screen — without
  // it the thead would float with no upper edge during sticky.
  const dayHeaderStyle = {
    padding: "10px 4px",
    fontSize: 12,
    fontWeight: 700,
    color: "#374151",
    textAlign: "center",
    background: "#F9FAFB",
    position: "sticky",
    top: stickyTop,
    zIndex: 5,
    boxShadow: "inset 0 1px 0 #D1D5DB, inset -1px 0 0 #D1D5DB, inset 0 -2px 0 #9CA3AF",
  };

  const cellStyle = {
    padding: "14px 4px",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    borderRight: GRID,
    borderBottom: GRID,
  };

  // Truncate helper: keeps a single line and adds ellipsis when the name overflows
  // the now-narrower Conductor/Dueño columns.
  const truncStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  };

  return (
    <div style={{ borderRadius: 8, background: "white", border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        {/* Fixed widths for the metadata columns; day columns flex to fill the rest equally. */}
        <colgroup>
          <col style={{ width: 90 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
          {DAY_COLUMNS.map((c, idx) => (
            <col key={`col-${idx}`} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...dayHeaderStyle, textAlign: "left", padding: "10px 12px" }}>Placa</th>
            <th style={{ ...dayHeaderStyle, textAlign: "left", padding: "10px 12px" }}>Conductor</th>
            <th style={{ ...dayHeaderStyle, textAlign: "left", padding: "10px 12px" }}>Dueño</th>
            {DAY_COLUMNS.map((col, idx) => {
              const date = weekDates[idx];
              const dateStr = date.getDate().toString().padStart(2, "0") + "/" + (date.getMonth() + 1).toString().padStart(2, "0");
              const isToday = formatIsoDate(date) === todayIso;
              return (
                <th
                  key={col.full + idx}
                  style={{
                    ...dayHeaderStyle,
                    background: isToday ? "#2563EB" : dayHeaderStyle.background,
                    color: isToday ? "white" : dayHeaderStyle.color,
                    // Today header: deeper bottom accent in dark blue.
                    boxShadow: isToday
                      ? "inset 0 1px 0 #D1D5DB, inset -1px 0 0 #D1D5DB, inset 0 -2px 0 #1E40AF"
                      : dayHeaderStyle.boxShadow,
                  }}
                >
                  <div style={{ fontSize: 13 }}>{col.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 500, color: isToday ? "rgba(255,255,255,0.85)" : "#6B7280", marginTop: 2 }}>{dateStr}</div>
                  {isToday && (
                    <div style={{ marginTop: 4, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, color: "white", background: "rgba(255,255,255,0.18)", borderRadius: 4, padding: "1px 6px", display: "inline-block" }}>HOY</div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {buses.map((bus, rowIdx) => {
            const sched = scheduleByBusId.get(bus.id);
            const rowBg = rowIdx % 2 === 0 ? "white" : "#FAFAFA";
            return (
              <tr key={bus.id} style={{ background: rowBg }}>
                <td style={{ padding: "12px 12px", borderRight: GRID, borderBottom: GRID, fontWeight: 700, color: "#3B82F6", fontSize: 13, ...truncStyle }} title={bus.plate}>
                  {bus.plate}
                </td>
                <td style={{ padding: "10px 12px", borderRight: GRID, borderBottom: GRID, fontSize: 12, color: "#374151" }} title={`${bus.driverDisplay} — ${bus.driverPhone}`}>
                  <div style={{ fontWeight: 600, ...truncStyle }}>{bus.driverDisplay}</div>
                  <div style={{ fontSize: 11, color: "#6B7280", ...truncStyle }}>{bus.driverPhone}</div>
                </td>
                <td style={{ padding: "10px 12px", borderRight: GRID, borderBottom: GRID, fontSize: 12, color: "#374151" }} title={`${bus.ownerDisplay} — ${bus.ownerPhone}`}>
                  <div style={{ fontWeight: 600, ...truncStyle }}>{bus.ownerDisplay}</div>
                  <div style={{ fontSize: 11, color: "#6B7280", ...truncStyle }}>{bus.ownerPhone}</div>
                </td>
                {DAY_COLUMNS.map((col, idx) => {
                  const date = weekDates[idx];
                  const iso = formatIsoDate(date);
                  const key = `${bus.id}:${iso}`;
                  const scheduled = sched ? !!sched[col.scheduleKey] : false;
                  const worked = activityKeys.has(key) || realtimeKeys.has(key);
                  const isFuture = iso > todayIso;
                  const cell = resolveCell(scheduled, worked, isFuture);
                  return (
                    <td
                      key={col.full + idx}
                      title={`${col.full} ${date.toLocaleDateString("es-VE")}: ${cell.label}`}
                      style={{
                        ...cellStyle,
                        background: cell.bg,
                        color: cell.color,
                      }}
                    >
                      {worked ? "✓" : scheduled ? (isFuture ? "•" : "✕") : ""}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {isLoadingTable && (
        <div style={{ padding: 12, textAlign: "center", fontSize: 12, color: "#6B7280" }}>
          Cargando datos de la semana...
        </div>
      )}
    </div>
  );
}
